import { keysToSnake } from '#/api/adapters/wireCase';
import {
  getSalesOrderLineQty,
  getSalesOrderTaxMode,
} from '#/modules/crmMgmt/salesOrder/helper/salesOrderFieldAccessors';
import { discountMultiplierToApiRate } from '#/utils/discountRate';

/**
 * 客戶訂單 create payload（camel → snake）。
 * 不送 customerOrderNo、不送衍生 totals。
 */
export function serializeCustomerOrderCreate(values) {
  const { customerOrderNo: _ignored, ...rest } = values ?? {};
  return keysToSnake(rest);
}

/**
 * 客戶訂單 update payload（camel → snake）。
 * 含 updatedAt（＝ record.updatedAt）。
 */
export function serializeCustomerOrderUpdate(values) {
  return keysToSnake(values);
}

function emptyToNull(value) {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? null : trimmed;
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}

/** 可解析數字才用；否則 0（create SO 勿送 null unit_price）。 */
function toUnitPriceOrZero(value) {
  if (value == null || value === '') {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * extra_items allowlist：itemName／actualQty／unitName／remark。
 * 勿送 customerOrderExtraItemSnapshotId（後端未知 → REQUEST_BODY_INVALID）。
 */
function mapExtraItemToCreatePayload(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }
  return {
    itemName: emptyToNull(item.itemName),
    actualQty: item.actualQty != null && item.actualQty !== ''
      ? toNumber(item.actualQty)
      : (
        item.referenceQty != null && item.referenceQty !== ''
          ? toNumber(item.referenceQty)
          : null
      ),
    unitName: emptyToNull(item.unitName),
    remark: emptyToNull(item.remark),
  };
}

function mapLineToCreatePayload(line) {
  const row = {
    productId: line?.productId ?? line?.product?.id ?? null,
    qty: toNumber(getSalesOrderLineQty(line) ?? line?.shipQty ?? line?.scheduledQty),
    unitPrice: toUnitPriceOrZero(line?.unitPrice),
    remark: emptyToNull(line?.remark),
    // 排程／經客訂建 DRAFT 不配置；固定 []，忽略表單殘值
    invAllocations: [],
    extraItems: (line?.extraItems ?? [])
      .map(mapExtraItemToCreatePayload)
      .filter(Boolean),
  };
  if (line?.discountRate != null && line.discountRate !== '') {
    const rate = toNumber(line.discountRate);
    if (Number.isFinite(rate)) {
      // 表單折數＝價格倍率；後端 discount_rate＝折抵比例，送出前轉換。空則省略（後端預設無折扣）。
      row.discountRate = discountMultiplierToApiRate(rate);
    }
  }
  if (line?.customerOrderLineId != null && line.customerOrderLineId !== '') {
    row.customerOrderLineId = line.customerOrderLineId;
  }
  return row;
}

/**
 * SO Create `manual_lines`：item／qty／unit／price／remark；可選 provenance。
 * 省略 id／cancelQty；勿塞進產品 lines[]。
 */
function mapManualLineToCreatePayload(line) {
  if (!line || typeof line !== 'object') {
    return null;
  }
  const row = {
    itemName: emptyToNull(line.itemName),
    qty: toNumber(line.qty ?? line.scheduledQty),
    unitName: emptyToNull(line.unitName),
    unitPrice: toUnitPriceOrZero(line.unitPrice),
    remark: emptyToNull(line.remark),
  };
  if (
    line.customerOrderManualLineId != null
    && line.customerOrderManualLineId !== ''
  ) {
    row.customerOrderManualLineId = line.customerOrderManualLineId;
  }
  return row;
}

/**
 * 客訂建銷貨單＝完整 DRAFT body（不送 amount totals／line.id／updatedAt）。
 * 地址／聯絡送三 ID；**不送** shippingAddress 字串／recipient_*（後端 ignore）。
 * `customerOrderLineId`／`customerOrderManualLineId` 可選 provenance。
 */
export function toCreateSalesOrderBody(values = {}) {
  return {
    plannedShipDate: emptyToNull(values.plannedShipDate),
    billingAddressId: emptyToNull(values.billingAddressId),
    shippingAddressId: emptyToNull(values.shippingAddressId),
    shippingContactId: emptyToNull(values.shippingContactId),
    taxMode: emptyToNull(getSalesOrderTaxMode(values) ?? values.priceTaxMode),
    taxRate: values.taxRate != null && values.taxRate !== ''
      ? toNumber(values.taxRate)
      : null,
    roundingAdjustment: values.roundingAdjustment != null && values.roundingAdjustment !== ''
      ? toNumber(values.roundingAdjustment)
      : null,
    issueReason: emptyToNull(values.issueReason),
    remark: emptyToNull(values.remark),
    lines: (values.lines ?? []).map(mapLineToCreatePayload),
    manualLines: (values.manualLines ?? [])
      .map(mapManualLineToCreatePayload)
      .filter(Boolean),
  };
}

/** POST `/crm/customer-order/{id}/sales-order` */
export function serializeCreateSalesOrderFromCustomerOrder(values) {
  return keysToSnake(toCreateSalesOrderBody(values));
}
